/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Add from '@material-ui/icons/Add';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {SimpleChip} from './SimpleChip';
import {QueryChipData} from './types';

interface QueryChipsProps {
  queries: QueryChipData[];
  onRemove?: Function;
  disabled?: boolean;
  showCount?: number;
  onClickQueryChip: (queryChipData: any) => void;
}

const styles = makeStyles(() => ({
  root : {
    marginBottom   : '10px',
    overflow       : 'unset',
    justifyContent : 'space-between',
    '&:hover'      : {
      backgroundColor : `${dlsColor('grey5')} !important`
    }
  },
  paper : {
    borderRadius : 8
  },
  deleteIcon : {
    marginTop : '6px'
  },
  list : {
    height    : 'auto',
    width     : 'auto',
    minHeight : '64px',
    maxHeight : '300px',
    minWidth  : '180px',
    maxWidth  : '400px',
    display   : 'grid',
    margin    : '8px 0 0px 8px'
  },
  iconButton : {
    padding    : '0 !important',
    color      : dlsColor('grey3'),
    fontSize   : 12,
    fontWeight : 700,
    whiteSpace : 'nowrap',
    '&:hover'  : {
      background : dlsColor('transparent')
    },
    '& .MuiButton-label' : {
      marginBottom : '10px'
    }
  },
  listItem : {
    height     : 40,
    minHeight  : 'auto',
    alignItems : 'baseline'
  },
  chipRoot : {
    background    : dlsColor('white'),
    border        : `1px solid ${dlsColor('caBlue')}`,
    display       : 'flex',
    flexDirection : 'row'
  },
  chipDeleteIcon : {
    margin : '0 !important',
    height : 16
  }
}));

const HIDDEN_QUERY = 'hidden-queries';

/**
 * Defines a reusable Query Chips.
 *
 * ```typescript
 * import React from 'react';
 * import {QueryChips} from '@ion/features/src/search/';
 *
 * <QueryChips queries={chips} onRemove={onQueryRemove} />
 * ```
 */

export const QueryChips = ({
  queries,
  onRemove,
  onClickQueryChip,
  disabled = false,
  showCount = 2
}: QueryChipsProps) => {
  const classes = styles();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const onIconClick = useCallback(function({currentTarget}) {
    setAnchorEl(currentTarget);
  }, []);
  const handleClose = useCallback(function() {
    setAnchorEl(null);
  }, []);
  const onChipRemove = useCallback(function(isPopOver = false) {
    return (attribute) => {
      return () => {
        onRemove(attribute)();
        if (isPopOver && queries.length - 1 <= 2) {
          handleClose();
        }
      };
    };
  }, [queries]);
  return (
    <>
      {!isEmpty(queries) && queries.slice(0, showCount)
        .map((query: QueryChipData, index: number) => (
          <SimpleChip key={`${query.attribute}-${index}`}
            classes={{root: classes.chipRoot, deleteIcon: classes.chipDeleteIcon}}
            query={query}
            onRemove={onChipRemove(false)(query.attribute)}
            onClickChip={onClickQueryChip}
            disabled={disabled} />
        ))}
      {queries?.length > showCount && (
        <Button classes={{root: classes.iconButton}}
          onClick={onIconClick}
          size='small'
          startIcon={<Add />}>
          <span>{translate('common:modularsearch.more', {count: queries?.length - showCount})}</span>
        </Button>
      )}
      {open && queries.length > 2 && (<Menu id={HIDDEN_QUERY}
        anchorEl={anchorEl}
        keepMounted
        open={open}
        anchorOrigin={{
          vertical   : 'top',
          horizontal : 'left'
        }}
        classes={{paper: classes.paper, list: classes.list}}
        onClose={handleClose}>
        {queries.slice(showCount, queries.length).map((query: QueryChipData, index: number) => (
          <MenuItem key={`${query.attribute}-${3+index}`}
            classes={{root: classes.listItem}}>
            <SimpleChip classes={classes}
              query={query}
              onClickChip={onClickQueryChip}
              onRemove={onChipRemove(true)(query.attribute)}
              disabled={disabled} />
          </MenuItem>
        ))}
      </Menu>
      )}
    </>
  );
};
